/**
 * CHI TIẾT KPI — the per-rule explanation.
 *
 * This is the screen the whole KPI module has to earn trust with. It renders
 * `kpi_score_details` VERBATIM: one row per rule, each carrying the raw value
 * the rule measured, the weight applied, the resulting points, and the
 * Vietnamese sentence the engine wrote (`explanation_vi`). Points added and
 * points deducted are separated, so "why did I lose points?" is answered
 * without scrolling.
 *
 * Nothing is computed here. If a total does not match the sum of the rows, the
 * screen shows both and says so, because the honest answer to a disagreement is
 * to surface it, not to hide it behind a recomputed number.
 *
 * `GET /staff/kpi` returns `kpiScoreId` (the `kpi_scores` row id), which is
 * passed through router state so an appeal filed from here references the
 * exact score row it disputes.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useKpi, errorMessage } from '../lib/queries';
import { formatBp, formatMinutes, formatPointsMilli, formatTimeDate } from '../lib/format';
import type { KpiPeriodType, KpiScoreDetail } from '../lib/contracts';
import { Button, Card, EmptyState, ErrorState, KeyValue, LoadingState, PointsText, SectionHeader, StatusBadge } from '../components/ui';
import { ScreenHeader } from '../components/ScreenHeader';

const PERIODS: readonly { value: KpiPeriodType; label: string }[] = [
  { value: 'DAY', label: 'Hôm nay' },
  { value: 'WEEK', label: 'Tuần' },
  { value: 'MONTH', label: 'Tháng' },
];

export function KpiDetailScreen(): JSX.Element {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<KpiPeriodType>('DAY');
  // No periodKey: the server derives the current one from the branch business
  // date, which is the only place that knows the branch's cutoff hour.
  const { data, isLoading, isError, error, refetch } = useKpi(period);

  const [added, deducted] = useMemo(() => {
    const rows = data?.details ?? [];
    return [
      rows.filter((d) => d.pointsMilli > 0),
      rows.filter((d) => d.pointsMilli < 0),
    ] as const;
  }, [data]);

  const neutral = (data?.details ?? []).filter((d) => d.pointsMilli === 0);
  const detailSum = (data?.details ?? []).reduce((sum, d) => sum + d.pointsMilli, 0);

  return (
    <div className="pb-8">
      <ScreenHeader title="Chi tiết KPI" subtitle={data?.periodKey} />

      <div className="mb-3 grid grid-cols-3 gap-2">
        {PERIODS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => setPeriod(option.value)}
            className={[
              'min-h-touch rounded-2xl border font-semibold active:bg-slate-50',
              option.value === period ? 'border-brand-500 bg-brand-50 text-brand-800' : 'border-slate-200 text-slate-700',
            ].join(' ')}
          >
            {option.label}
          </button>
        ))}
      </div>

      {isLoading ? <LoadingState /> : null}
      {isError ? <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} /> : null}

      {data === undefined || isLoading ? null : data.score === null ? (
        <EmptyState
          title={data.message ?? 'Chưa có điểm KPI cho kỳ này'}
          description="Điểm được tính sau khi hệ thống tổng hợp các thao tác của bạn trong kỳ. Nếu bạn đã làm việc trong kỳ này mà vẫn chưa có điểm, hãy hỏi quản lý chi nhánh."
        />
      ) : (
        <>
          {/* --- headline ------------------------------------------------ */}
          <Card>
            <div className="flex items-end justify-between gap-3">
              <div>
                <p className="text-sm text-slate-500">Tổng điểm</p>
                <p className="text-4xl font-black tabular-nums text-slate-900">
                  {formatPointsMilli(data.score.totalPointsMilli)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">Xếp hạng</p>
                <p className="text-2xl font-black tabular-nums text-brand-700">
                  {data.score.rankInBranch === null ? '—' : `#${data.score.rankInBranch}`}
                </p>
                <p className="text-xs text-slate-500">trong chi nhánh</p>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-3 gap-2 border-t border-slate-100 pt-3 text-center">
              <div>
                <p className="text-xs text-slate-500">Điểm nền</p>
                <p className="font-bold tabular-nums">{formatPointsMilli(data.score.basePointsMilli)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Điểm thưởng</p>
                <p className="font-bold tabular-nums text-emerald-700">
                  {formatPointsMilli(data.score.bonusPointsMilli)}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Điểm trừ</p>
                <p className="font-bold tabular-nums text-rose-700">
                  {formatPointsMilli(data.score.penaltyPointsMilli)}
                </p>
              </div>
            </div>

            <div className="mt-3 divide-y divide-slate-100 border-t border-slate-100">
              <KeyValue label="Số việc" value={data.score.taskCount} />
              <KeyValue label="Đúng hạn" value={data.score.onTimeCount} />
              <KeyValue label="Trễ hạn" value={data.score.lateCount} />
              <KeyValue label="Từ chối" value={data.score.rejectedCount} />
              <KeyValue label="Bỏ dở" value={data.score.abandonedCount} />
              <KeyValue label="Thời gian làm" value={formatMinutes(data.score.workedMinutes)} />
              <KeyValue
                label="Hệ số bận rộn"
                hint="Ca đông khách được nhân hệ số cao hơn"
                value={formatBp(data.score.busynessFactorBp)}
              />
              {data.score.percentileBp === null ? null : (
                <KeyValue label="Vị trí phân vị" value={formatBp(data.score.percentileBp)} />
              )}
              <KeyValue label="Xếp hạng theo vai trò" value={data.score.rankInRole === null ? '—' : `#${data.score.rankInRole}`} />
            </div>

            <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
              <StatusBadge tone={data.score.isFinal ? 'success' : 'warning'}>
                {data.score.isFinal ? 'Điểm đã chốt' : 'Điểm tạm tính'}
              </StatusBadge>
              <span className="text-xs text-slate-500">
                Tính lúc {formatTimeDate(data.score.computedAt)}
                {data.score.ruleVersion === null ? '' : ` · bộ quy tắc v${data.score.ruleVersion}`}
              </span>
            </div>
          </Card>

          {/* --- the per-rule explanation -------------------------------- */}
          <SectionHeader title={`Điểm được cộng (${added.length})`} />
          {added.length === 0 ? (
            <EmptyState title="Chưa có quy tắc nào cộng điểm trong kỳ này" />
          ) : (
            <ul className="space-y-2">
              {added.map((detail) => (
                <li key={`${detail.ruleCode}:${detail.metric}`}>
                  <RuleRow detail={detail} />
                </li>
              ))}
            </ul>
          )}

          <SectionHeader title={`Điểm bị trừ (${deducted.length})`} />
          {deducted.length === 0 ? (
            <EmptyState title="Bạn không bị trừ điểm nào trong kỳ này" />
          ) : (
            <ul className="space-y-2">
              {deducted.map((detail) => (
                <li key={`${detail.ruleCode}:${detail.metric}`}>
                  <RuleRow detail={detail} />
                </li>
              ))}
            </ul>
          )}

          {neutral.length === 0 ? null : (
            <>
              <SectionHeader title={`Quy tắc không ảnh hưởng điểm (${neutral.length})`} />
              <ul className="space-y-2">
                {neutral.map((detail) => (
                  <li key={`${detail.ruleCode}:${detail.metric}`}>
                    <RuleRow detail={detail} />
                  </li>
                ))}
              </ul>
            </>
          )}

          {detailSum !== data.score.totalPointsMilli ? (
            <p className="mt-3 rounded-2xl bg-amber-50 p-3 text-sm text-amber-900">
              Tổng các dòng chi tiết ({formatPointsMilli(detailSum)}) khác với tổng điểm của kỳ (
              {formatPointsMilli(data.score.totalPointsMilli)}). Điều này xảy ra khi có điều chỉnh thủ công
              hoặc kết quả khiếu nại. Hãy hỏi quản lý hoặc gửi khiếu nại nếu bạn thấy chưa hợp lý.
            </p>
          ) : null}

          <Button
            variant="secondary"
            size="lg"
            fullWidth
            className="mt-6"
            onClick={() =>
              navigate('/ca-nhan/khieu-nai', {
                state: {
                  appealType: 'KPI_SCORE',
                  kpiScoreId: data.kpiScoreId ?? undefined,
                  periodLabel: `${data.periodType} ${data.periodKey}`,
                },
              })
            }
          >
            Khiếu nại điểm KPI kỳ này
          </Button>
        </>
      )}
    </div>
  );
}

/**
 * One rule, fully unpacked. `explanation_vi` is the sentence the KPI engine
 * itself wrote; the numbers beside it are what it measured, so an employee can
 * argue with the input rather than with the verdict.
 */
function RuleRow({ detail }: { readonly detail: KpiScoreDetail }): JSX.Element {
  const [open, setOpen] = useState(false);
  const evidenceEntries = Object.entries(detail.evidence);

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold text-slate-900">{detail.metric}</p>
          <p className="font-mono text-xs text-slate-400">{detail.ruleCode}</p>
        </div>
        <PointsText pointsMilli={detail.pointsMilli} />
      </div>

      {detail.explanation === null ? (
        <p className="mt-2 text-sm italic text-slate-500">Quy tắc này chưa có diễn giải bằng lời.</p>
      ) : (
        <p className="mt-2 text-sm text-slate-700">{detail.explanation}</p>
      )}

      <div className="mt-2 grid grid-cols-3 gap-2 rounded-xl bg-slate-50 p-2 text-center text-xs">
        <div>
          <p className="text-slate-500">Giá trị đo</p>
          <p className="font-semibold tabular-nums">{formatPointsMilli(detail.rawValueMilli)}</p>
        </div>
        <div>
          <p className="text-slate-500">Sau chuẩn hoá</p>
          <p className="font-semibold tabular-nums">{formatPointsMilli(detail.normalizedValueMilli)}</p>
        </div>
        <div>
          <p className="text-slate-500">Trọng số</p>
          <p className="font-semibold tabular-nums">{formatBp(detail.weightBp)}</p>
        </div>
      </div>

      <p className="mt-2 text-xs text-slate-500">Dựa trên {detail.sampleCount} bản ghi.</p>

      {evidenceEntries.length === 0 && detail.sourceQueryRef === null ? null : (
        <>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="tap mt-1 text-sm font-semibold text-brand-700"
          >
            {open ? 'Ẩn dữ liệu gốc' : 'Xem dữ liệu gốc'}
          </button>
          {open ? (
            <div className="mt-2 rounded-xl bg-slate-100 p-3 text-xs text-slate-700">
              {detail.sourceQueryRef === null ? null : (
                <p className="mb-2 font-mono break-all">Nguồn: {detail.sourceQueryRef}</p>
              )}
              <ul className="space-y-1">
                {evidenceEntries.map(([key, value]) => (
                  <li key={key} className="break-all">
                    <span className="text-slate-500">{key}: </span>
                    <span className="font-mono">{JSON.stringify(value)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
}
